<script setup>
import Multiselect from "vue-multiselect";
import {computed, inject, onMounted, ref, watch} from "vue";
import trackersService from "@/services/trackers.service";
import CustomFieldEdit from "@/components/customFields/edit/CustomFieldEdit.vue";
import customFieldsService from "@/services/custom-fields.service";

const tracker = inject('tracker');
const trackerId = inject('trackerId');
const canEditInfo = inject('canEditInfo');
const currentTimeline = ref(null);
const timelinesList = ref([]);
const editPOCModal = ref(null);
const newPeriod = ref(null);
const addPeriodModal = ref(null);
const showAddPeriodModal = ref(false);
const periodsList = ref([]);

const actualPeriodsList = computed(() =>
    periodsList.value
        .filter(field => !tracker.subPeriods.some(item => item.id === field.id))
);

watch(() => tracker.timelinePreset, (value) => {
    if (value) {
        if (value.id === -1) {
            tracker.timelinePreset = null;
            editPOCModal.value.open(0);
        } else {
            currentTimeline.value = value;
        }
    }
}, {immediate: true});

watch(newPeriod, (value) => {
    if (value?.id === -1) {
        showAddPeriodModal.value = false;
        addPeriodModal.value.open(0);
    }
});

onMounted(() => {
    getTimelinesList();
    getPeriodsList();
});

async function getTimelinesList() {
    const {data} = await trackersService.getTimelinePresets();

    timelinesList.value = data;

    if (timelinesList.value.length && !trackerId.value) {
        tracker.timelinePreset = timelinesList.value[0];
    }

    timelinesList.value.push({
        id: -1,
        name: "Create New Preset",
        statuses: [],
    });
}

async function getPeriodsList() {
    const {data} = await customFieldsService.getFieldsArray('tracker_sub_period');
    periodsList.value = data;

    periodsList.value.push({
        id: -1,
        name: "Create New Period",
    })
}

async function updateTimeline(id) {
    const {data} = await trackersService.getTimelinePresets();

    timelinesList.value = [
        ...data,
        {
            id: -1,
            name: "Create New Funnel",
            statuses: [],
        }
    ];

    tracker.timelinePreset = timelinesList.value.find(item => item.id === id);
}

function resetTimeline() {
    tracker.timelinePreset = currentTimeline.value;
}

function addPeriod() {
    tracker.subPeriods.push({
        id: newPeriod.value.id,
        name: newPeriod.value.name,
    })

    newPeriod.value = null;
    showAddPeriodModal.value = false;
}

async function periodCreated(id) {
    await getPeriodsList();

    showAddPeriodModal.value = true;
    newPeriod.value = periodsList.value.find(item => item.id === id);
}

</script>

<template>
    <div>
        <div class="pipeline-cover">
            <span class="pipeline-cover__status">Tracker statuses:</span>

            <Multiselect
                class="multiselect-mid"
                v-model="tracker.timelinePreset"
                :options="timelinesList"
                select-label=""
                deselect-label=""
                label="name"
                track-by="name"
                :disabled="!canEditInfo"
            />

            <div class="link-wrapper m-0 p-0">
                <a
                    v-if="tracker.timelinePreset && canEditInfo"
                    class="link-wrapper__edit text-nowrap"
                    @click="editPOCModal.open(tracker.timelinePreset.id)"
                >Edit Template</a>
            </div>
        </div>

        <template v-if="tracker.timelinePreset">
            <ul class="rows-list rows-list--pipeline list-colors">
                <li v-for="(status, index) in tracker.timelinePreset.statuses" :key="index">
                    {{status.name}}

                    <div class="list-colors__input">
                        <span class="list-colors__input__text">Color:</span>

                        <div class="list-colors__input__wrapper">
                            <input type="color" disabled :value="status.color">
                        </div>
                    </div>
                </li>
            </ul>

            <div class="d-flex justify-content-between">
                <h5 class="heading5 heading5--secondary">Sub-Dates</h5>

                <div class="link-wrapper">
                    <a @click="showAddPeriodModal = true" class="link">+ Add</a>
                </div>
            </div>

            <ul v-if="tracker.subPeriods.length" class="rows-list rows-list--summary">
                <li v-for="(period, index) in tracker.subPeriods" :key="period.id">
                    {{period.name}}

                    <a @click="tracker.subPeriods.splice(index, 1)" class="link-remove link" href="#"></a>
                </li>
            </ul>
        </template>

        <CustomFieldEdit
            ref="editPOCModal"
            type="tracker_diligence"
            fieldType="POC_preset"
            @saved="updateTimeline"
            @closedWithoutChanges="resetTimeline"
        />

        <CustomFieldEdit
            ref="addPeriodModal"
            type="tracker_sub_period"
            @saved="periodCreated"
            @closedWithoutChanges="showAddPeriodModal = true"
        />

        <b-modal v-model="showAddPeriodModal" title="Add Timeline">
            <select v-model="newPeriod" class="selectbox selectbox-mid">
                <option :value="null">
                    Select Timeline
                </option>

                <option v-for="(field, id) in actualPeriodsList" :key="id" :value="field">
                    {{field.name}}
                </option>
            </select>

            <template #modal-footer>
                <b-button variant="primary" @click="addPeriod" :disabled="!newPeriod">Add</b-button>
            </template>
        </b-modal>
    </div>
</template>

<style scoped>

</style>
